import { Info, PersonAdd } from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useWindowWidth } from '@react-hook/window-size';
import {
  QueryFilters,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import { captureError } from '../../utils/capture-error';
import { validateEmail, validatePassword } from '../../utils/validation';

export const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#03182E',
    color: '#f5f5f9',
    maxWidth: 250,
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Lato',
    borderRadius: 10,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#03182E',
  },
}));

export function StaffAdd(props: { queryKey: QueryFilters['queryKey'] }) {
  // Hooks
  const width = useWindowWidth();
  // State
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState({
    Username: '',
    FName: '',
    LName: '',
    Email: '',
    Password: '',
    ConfirmPassword: '',
  });

  const queryClient = useQueryClient();
  // Mutation - Add New Staff Account
  const mutation = useMutation({
    mutationFn: async () => {
      await API.post('ContractingAPI', '/my-staff/add', {
        body: {
          Username: data.Username,
          FName: data.FName,
          LName: data.LName,
          Email: data.Email,
          TempPassword: data.Password,
        },
      });
    },
    onSuccess: async () => {
      // Refetch staff list
      await queryClient.invalidateQueries({ queryKey: props.queryKey });
    },
    onError: (error) => captureError({ data: { error }, removeData: true }),
  });

  const handleToggle = async () => {
    setData({
      Username: '',
      FName: '',
      LName: '',
      Email: '',
      Password: '',
      ConfirmPassword: '',
    });
    mutation.reset();
    setOpen((open) => !open);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };

  const invalidUsername = data.Username.toLowerCase().substring(0, 3) === 'naa';
  const mismatchPass = data.Password !== data.ConfirmPassword;

  return (
    <React.Fragment>
      <Button
        variant="contained"
        size="small"
        startIcon={width >= 450 ? <PersonAdd /> : null}
        onClick={() => setOpen(true)}
        sx={{ ml: 2, whiteSpace: 'nowrap' }}
      >
        {width >= 450 ? 'Add Staff' : <PersonAdd fontSize="small" />}
      </Button>
      <Drawer
        container={window.document.body}
        variant="temporary"
        anchor="right"
        open={open}
        onClose={handleToggle}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: { xs: '100vw', sm: '80vw', md: '60vw', lg: '40vw' },
            height: '100dvh',
          },
        }}
      >
        <Box
          sx={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}
        >
          <Toolbar sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              aria-label="close"
              sx={{ mr: 2 }}
              onClick={handleToggle}
            >
              <Close />
            </IconButton>
            <Box sx={{ fontSize: 22 }}>Add New Staff Account</Box>
          </Toolbar>

          <Divider />

          <Box
            sx={{
              minHeight: 0,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack spacing={2} sx={{ p: 2 }}>
              <Box>
                <TextField
                  fullWidth
                  label="Username"
                  name="Username"
                  onChange={handleChange}
                  required
                  value={data.Username}
                  style={{
                    backgroundColor: '#fff',
                  }}
                  error={invalidUsername}
                  autoComplete="off"
                />
                {invalidUsername ? (
                  <Typography
                    variant="caption"
                    style={{
                      color: '#D32F2F',
                      paddingLeft: 10,
                    }}
                  >
                    Username can not start with 'NAA'
                  </Typography>
                ) : null}
              </Box>

              <TextField
                fullWidth
                label="First Name"
                name="FName"
                onChange={handleChange}
                required
                value={data.FName}
                style={{
                  backgroundColor: '#fff',
                }}
              />

              <TextField
                fullWidth
                label="Last Name"
                name="LName"
                onChange={handleChange}
                required
                value={data.LName}
                style={{
                  backgroundColor: '#fff',
                }}
              />

              <Box>
                <TextField
                  fullWidth
                  label="Email"
                  name="Email"
                  type="email"
                  onChange={handleChange}
                  required
                  value={data.Email}
                  style={{ backgroundColor: '#fff' }}
                  error={validateEmail({ email: data.Email })}
                />
                {validateEmail({ email: data.Email }) ? (
                  <Typography
                    variant="caption"
                    style={{
                      color: '#D32F2F',
                      paddingLeft: 10,
                    }}
                  >
                    Invalid Email
                  </Typography>
                ) : null}
              </Box>

              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Temporary Password</InputLabel>
                <OutlinedInput
                  label="Temporary Password"
                  name="Password"
                  onChange={handleChange}
                  value={data.Password}
                  style={{ backgroundColor: '#fff' }}
                  error={validatePassword({ password: data.Password })}
                  autoComplete="off"
                  endAdornment={
                    <HtmlTooltip
                      arrow
                      placement="top"
                      title={
                        <div style={{ padding: 5 }}>
                          Password <strong>MUST</strong> include:
                          <ul style={{ marginTop: 0 }}>
                            <li> 14+ characters</li>
                            <li> uppercase letter</li>
                            <li>lowercase letter</li>
                            <li>number</li>
                            <li>special character</li>
                          </ul>
                          Password must <strong>NOT</strong> contain
                          <ul style={{ marginTop: 0 }}>
                            <li>your name</li>
                            <li>your agent number</li>
                          </ul>
                        </div>
                      }
                    >
                      <Info fontSize="small" />
                    </HtmlTooltip>
                  }
                />
                {validatePassword({ password: data.Password }) ? (
                  <Typography
                    variant="caption"
                    style={{
                      color: '#D32F2F',
                      paddingLeft: 10,
                    }}
                  >
                    Invalid Password
                  </Typography>
                ) : null}
              </FormControl>

              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Confirm Password</InputLabel>
                <OutlinedInput
                  label="Confirm Password"
                  name="ConfirmPassword"
                  onChange={handleChange}
                  value={data.ConfirmPassword}
                  style={{ backgroundColor: '#fff' }}
                  autoComplete="off"
                  error={
                    validatePassword({
                      password: data.ConfirmPassword,
                    }) || mismatchPass
                  }
                />
                {mismatchPass ? (
                  <Typography
                    variant="caption"
                    style={{
                      color: '#D32F2F',
                      paddingLeft: 10,
                    }}
                  >
                    Passwords do not match
                  </Typography>
                ) : null}
              </FormControl>
            </Stack>
          </Box>

          <Divider />

          {mutation.isSuccess ? (
            <Alert severity="success">
              <AlertTitle>Success</AlertTitle>
              New staff account created!
            </Alert>
          ) : null}

          {mutation.isError ? (
            <Alert severity="error" onClose={() => mutation.reset()}>
              <AlertTitle>Error</AlertTitle>
              {
                // @ts-ignore
                mutation.error?.response?.data?.error ||
                  'An error has occurred while trying to add new staff account'
              }
            </Alert>
          ) : null}

          <Button
            variant="contained"
            size="large"
            fullWidth
            color="info"
            disabled={
              Object.values(data).includes('') ||
              validateEmail({ email: data.Email }) ||
              validatePassword({ password: data.Password }) ||
              invalidUsername ||
              mismatchPass ||
              mutation.isError
            }
            sx={{ borderRadius: 0, minHeight: 64 }}
            onClick={() => mutation.mutate()}
          >
            {mutation.isLoading ? 'Adding...' : 'Add Account'}
          </Button>
        </Box>
      </Drawer>
    </React.Fragment>
  );
}
